import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../ThemeContext';
import { getProjects, deleteProject, changeStatus } from '../../api/api';
import { MantineProvider, Select, Button } from '@mantine/core';
import { PencilIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../api/services';
import AddProject from '../AddProject';

const Example = ({ update }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [data, setData] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  // console.log(data);
  const columns = useMemo(() => [
    {
      accessorKey: 'project_name',
      header: 'Name',
      Cell: ({ row, cell }) => {
        return (
          <p className="hover:cursor-pointer hover:underline text-blue-400 font-semibold"
            onClick={() => navigate('/Projects/Details', { state: { data: row.original } })}>{cell.getValue()}</p>
        )
      }
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
    },
    {
      accessorKey: 'project_duration',
      header: 'Project Duration',
    },
    {
      accessorKey: 'project_manager',
      header: 'Project Manager',
      Cell: ({ cell, row }) => {
        return (

          <p className="hover:cursor-pointer hover:underline "
            onClick={() => navigate('/Users/details', { state: { UserId: row.original.project_manager._id } })} >{cell.getValue()}</p>
        )
      }
    },
    {
      accessorKey: 'client_side_manager',
      header: 'Client Manager',
      Cell: ({ cell, row }) => {
        return (

          <p className="hover:cursor-pointer hover:underline "
            onClick={() => navigate('/Users/details', { state: { UserId: row.original.client_side_manager._id } })} >{cell.getValue()}</p>
        )
      }
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      Cell: ({ row }) => formatDate(row.getValue('start_date')),
    },
    {
      accessorKey: 'end_date',
      header: 'Estimated Date',
      Cell: ({ row }) => formatDate(row.getValue('end_date')),
    },
    {
      accessorKey: 'actual_end_date',
      header: 'End Date',
      Cell: ({ row }) => formatDate(row.getValue('actual_end_date')),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        const status = row.getValue('status');

        return (
          <Select
            placeholder="Select Status"
            value={status}
            onChange={(value) => handleStatusChange(row, value)}
            data={[
              { value: 'Started', label: 'Started' },
              { value: 'Phase 1', label: 'Phase 1' },
              { value: 'Phase 2', label: 'Phase 2' },
              { value: 'Phase 3', label: 'Phase 3' },
              { value: 'Completed', label: 'Completed' },
            ]}
            searchable
            styles={{
              input: {
                fontSize: '0.875rem',
                padding: '5px 10px',
                color: (status === 'Completed' ? 'green' : 'black'),
              },
              dropdown: {
                backgroundColor: '#2d3748',
              },
              item: {
                '&[data-selected]': {
                  backgroundColor: '#3182ce',
                  color: 'white',
                },
                '&:hover': {
                  backgroundColor: theme === 'dark' ? '#4a5568' : 'lightgray',
                },
              },
            }}
            dropdownPosition="bottom"
            transition="pop-top-left"
            transitionDuration={150}
            withinPortal
          />
        );
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <PencilIcon
            className="w-5 h-5 text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleEditRow(row)}
          />
          <EyeIcon
            className="w-5 h-5 text-green-600 hover:text-green-800 cursor-pointer"
            onClick={() => navigate('/Projects/Details', { state: { data: row.original } })}
          />
          <TrashIcon
            className="w-5 h-5 text-red-600 hover:text-red-800 cursor-pointer"
            onClick={() => openDeleteConfirmation(row)}
          />
        </div>
      ),
    },
  ], []);

  const handleStatusChange = async (row, status) => {
    try {
      await changeStatus(row.original._id, status);
      setData((prevData) => prevData.map((project) =>
        project._id === row.original._id ? { ...project, status } : project
      ));
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const openDeleteConfirmation = (row) => {
    setProjectToDelete(row.original);
    setConfirmModalOpen(true);
  };

  const handleDeleteRow = async () => {
    if (projectToDelete) {
      try {
        await deleteProject(projectToDelete._id);
        setData((prevData) => prevData.filter((project) => project._id !== projectToDelete._id));
        setConfirmModalOpen(false);
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleEditRow = (row) => {
    setEditingRow(row.original);
    setEditing(true);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getProjects();
        setData(response.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, [update]);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnActions: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableRowNumbers: true,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      sx: {
        'thead > tr': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
        },
        'thead > tr > th': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
        'tbody > tr > td': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
      },
    },
  });

  return (
    <MantineProvider theme={{ colorScheme: theme, primaryColor: 'blue', primaryShade: 3 }}>
      <MantineReactTable table={table} />
      {editing && (
        <AddProject
          data={editingRow}
          isEditing={editing}
          setIsEditing={setEditing}
        />
      )}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteRow}
        title="Confirm Deletion"
        message="Are you sure you want to delete this project?"
      />
    </MantineProvider>
  );
};

export default Example;
